import { useEffect, useState } from 'react';
import {
  Component as ContentFulComponent,
  Layout as ContentFulLayout,
  TextItem,
  enabledLocales,
  VariationContainer,
  AUTH_OPTIMIZELY_EXPERIMENT_ID,
} from '@lib/contentful/types';
import { getContentfulData } from '@src/lib/contentful/index';
import { getDefaultValueProps } from '@src/lib/defaultValueProps';
import { ValuePropsData, ValuePropType } from '@src/components/ValueProps/types';
import { OneAuthProps } from '@chegg/one-auth';
import { getOptimizelyClient } from '@lib/optimizley';
import { Locale, DEFAULT_LOCALE, OFSParams, OFSParamsObj } from '../lib/contentful/types';
import Cookies from 'js-cookie';
import { ReactSDKClient } from '@optimizely/react-sdk';

export type Layout = {
  valueProps: ValuePropsData;
  oneAuthProps: OneAuthProps;
};

export function useFetchContentfulLayout(
  setOneAuthProps: (oneAuthProps: any) => void,
  valuePropsType?: string | false,
  query?: any,
  locale?: Locale
) {
  const [layout, setLayout] = useState<Layout>();

  useEffect(() => {
    if (!valuePropsType) {
      return;
    }
    const layoutType = valuePropsType;

    // exception for pla
    if (layoutType === 'pla') {
      setLayout({
        valueProps: {
          ...getDefaultValueProps(layoutType),
        },
        oneAuthProps: {} as OneAuthProps,
      });
      return;
    }

    getContentfulData<ContentFulLayout>({
      content_type: 'authLayout',
      'fields.uniqueId': layoutType,
      locale: enabledLocales.includes(locale as Locale) ? locale : DEFAULT_LOCALE,
      include: 10,
    })
      .then((entries) => {
        const { items } = entries;
        if (!items.length) {
          console.log('no items found for type', valuePropsType);
          return;
        }

        const layout = items[0];
        const {
          fields: { components },
        } = layout;

        if (!components || !components.length) {
          setLayout({
            valueProps: {
              ...getDefaultValueProps((layoutType as ValuePropType) || 'simplifiedstudy'),
            },
            oneAuthProps: {} as OneAuthProps,
          });
          return;
        }

        let valuePropsData: ValuePropsData = {};
        let oneAuthData = {} as OneAuthProps;
        const optimizelyClient = getOptimizelyClient();
        // process normal contentful components
        (components as ContentFulComponent[])
          .filter((component) => component.fields.uniqueId)
          .forEach((component) => {
            const { oneAuthData: newOneAuthData, valuePropsData: newValuePropsData } =
              processContentFulComponent(
                component,
                valuePropsData,
                oneAuthData,
                layoutType as ValuePropType
              );
            oneAuthData = newOneAuthData;
            valuePropsData = newValuePropsData;
          });

        const userId = Cookies.get('CVID') || null;

        let forcedOFSParams = [] as OFSParams;
        if (!forcedOFSParams && checkForceOptimizelyQueryParams(query)) {
          const ofsParams = getForcedOfsParams(query);
          forcedOFSParams = ofsParams;
        }

        // process variation components
        // https://www.contentful.com/developers/docs/tutorials/general/optimizely-tutorial/
        (components as VariationContainer[])
          .filter((component) => component.fields.experimentKey)
          .forEach((variationContainer) => {
            const { experimentKey } = variationContainer.fields;

            optimizelyClient.onReady().then(() => {
              if (forcedOFSParams?.length) {
                const forcedOFSParamsObject = hasForcedOfsParams(
                  forcedOFSParams,
                  AUTH_OPTIMIZELY_EXPERIMENT_ID as string
                );
                if (Object.keys(forcedOFSParamsObject).length) {
                  forceSetOptimizely(
                    optimizelyClient,
                    forcedOFSParamsObject as OFSParamsObj,
                    userId as string
                  );
                }
              }

              const variation = optimizelyClient.activate(experimentKey, userId as string, {
                browser_language: locale, // check audience
              });
              if (!variation) return;
              const variationId = variationContainer.fields.meta[variation];

              if (!variationContainer.fields.variations) return;
              const variationComponent = variationContainer.fields.variations.find(
                (comp) => comp.sys.id === variationId
              );
              if (!variationComponent) return;
              const { oneAuthData: newOneAuthData, valuePropsData: newValuePropsData } =
                processContentFulComponent(
                  variationComponent,
                  valuePropsData,
                  oneAuthData,
                  layoutType as ValuePropType
                );
              oneAuthData = newOneAuthData;
              valuePropsData = newValuePropsData;
              setLayout({
                valueProps: valuePropsData,
                oneAuthProps: oneAuthData,
              });
            });
          });

        setOneAuthProps((prevProps: any) => {
          return { ...prevProps, ...oneAuthData };
        });
        setLayout({
          valueProps: valuePropsData,
          oneAuthProps: oneAuthData,
        });
      })
      .catch((error) => {
        console.error(error);
      });
  }, [locale, query, setOneAuthProps, valuePropsType]);

  return layout;
}

function parseTextItems(valuePropsType: ValuePropType, textItems?: TextItem[]) {
  if (!textItems) {
    return getDefaultValueProps(valuePropsType);
  }
  const valuePropsData: ValuePropsData = {};
  textItems.forEach((textItem) => {
    const {
      fields: { text, uniqueId },
    } = textItem;
    if (uniqueId.includes('.header')) {
      valuePropsData.header = text;
    }
    if (uniqueId.includes('.subheader')) {
      valuePropsData.subheader = text;
    }
    if (uniqueId.includes('.footer')) {
      valuePropsData.footer = text;
    }
    if (uniqueId.includes('.bullet')) {
      const [, bullet] = uniqueId.split('.');
      if (bullet) {
        valuePropsData[bullet] = text;
      }
    }
    if (uniqueId.includes('.icon')) {
      const [, icon] = uniqueId.split('.');
      if (icon) {
        valuePropsData[icon] = text;
      }
    }
  });
  return valuePropsData;
}

function processContentFulComponent(
  component: ContentFulComponent,
  valuePropsData: ValuePropsData,
  oneAuthData: OneAuthProps,
  layoutType: ValuePropType
) {
  const {
    fields: { uniqueId, textItems, valuePropsProps, oneAuthProps },
  } = component;

  if (uniqueId.startsWith('valueprops')) {
    valuePropsData = {
      styles: valuePropsProps?.styles || {},
      ...parseTextItems(layoutType as ValuePropType, textItems),
    };
  }

  if (uniqueId.startsWith('oneauth')) {
    oneAuthData = (oneAuthProps as OneAuthProps) || {};
    if (textItems?.length) {
      textItems.forEach((textItem) => {
        const {
          fields: { text, uniqueId },
        } = textItem;
        if (uniqueId.includes('simplifiedstudy.oneauth.signup.header')) {
          oneAuthData = {
            ...oneAuthData,
            options: {
              ...oneAuthData.options,
              signup: { ...oneAuthData.options?.signup, title: text },
            },
          };
        }
        if (uniqueId.includes('simplifiedstudy.oneauth.signup.submit')) {
          oneAuthData = {
            ...oneAuthData,
            options: {
              ...oneAuthData.options,
              signup: { ...oneAuthData.options?.signup, buttonTitle: text },
            },
          };
        }
        if (uniqueId.includes('simplifiedstudy.oneauth.signin.header')) {
          oneAuthData = {
            ...oneAuthData,
            options: {
              ...oneAuthData.options,
              signin: { ...oneAuthData.options?.signin, title: text },
            },
          };
        }
      });
    }
  }

  return {
    valuePropsData,
    oneAuthData,
  };
}

function getForcedOfsParams(query: any) {
  // fetch all params related to force Optimizely i.e. "force-exp-{experimentId}=variationId" from url.
  const forceRegex = /^force-exp-([\w-]+)$/;
  const forcedOfsParamsList = [] as any;
  Object.keys(query).forEach((key) => {
    if (forceRegex.test(key)) {
      const forcedParamsObj = {
        experimentId: key.slice(10), // removing 'force-exp-' from [force-exp-{experimentId} i.e. force-exp-landing_page_hero_image], to get experiment id.
        variationId: query[key],
      };
      forcedOfsParamsList.push(forcedParamsObj);
    }
  });
  return forcedOfsParamsList;
}

function checkForceOptimizelyQueryParams(query: any) {
  // fetch all params related to force Optimizely i.e. "force-exp-{experimentId}=variationId" from url.
  const forceRegex = /^force-exp-([\w-]+)$/;
  let isForceParamsPresent = false;
  Object.keys(query).forEach((key) => {
    if (forceRegex.test(key)) {
      isForceParamsPresent = true;
    }
  });
  return isForceParamsPresent;
}

function hasForcedOfsParams(forcedOFSParams: OFSParams, ofsExperimentId: string) {
  // return matching experimentId and variationId as an object.
  let optimizelyURLData = {}; // stores expermentId and VariationId passed as query param in url.
  forcedOFSParams.forEach((paramsObj: OFSParamsObj) => {
    if (paramsObj.experimentId === ofsExperimentId) {
      optimizelyURLData = paramsObj;
    }
  });
  return optimizelyURLData;
}

// This fn is used to call force optimizely method with the passed experimentId and variationId in url.
function forceSetOptimizely(
  optimizelyClient: ReactSDKClient,
  forcedParamsObj: OFSParamsObj,
  userId: string
) {
  const { experimentId, variationId } = forcedParamsObj;
  const experimentsMap = optimizelyClient.getOptimizelyConfig()?.experimentsMap;
  // @ts-ignore
  if (experimentsMap && Object.keys(experimentsMap).length) {
    const experiment = experimentsMap[experimentId];
    if (!experiment) return;
    const variationKeys = Object.keys(experiment.variationsMap);
    if (variationKeys.includes(variationId))
      optimizelyClient.setForcedVariation(experimentId, userId, variationId);
  }
}
